/**
 * Labstep
 */

import {
  PermissionRoleSections,
  PermissionRoleSettingsPostParameters,
} from 'labstep-web/constants/roles';
import { usePremiumFeatureStatus } from 'labstep-web/hooks/premium-feature';
import type { APICreateAction } from 'labstep-web/models';
import type { PermissionRole } from 'labstep-web/models/permission-role.model';
import {
  type PartialPermissionRoleSetting,
  type PermissionRoleSetting,
  PermissionRoleSettingType,
} from 'labstep-web/models/permission-role-setting.model';
import { getHumanReadableEntityName } from 'labstep-web/services/i18n.service';

/**
 * Human readable labels for each permission type
 */
export const LABELS_FOR_PERMISSIONS = {
  all: 'Always',
  none: 'Never',
  author: 'Author',
  assigned: 'If Assigned',
  if_assigned: 'If Assigned',
  if_assigned_to_collection: 'If Assigned To Collection',
};

/**
 * @description Sections for TableSimpleCollapsible
 */
export const getSections = () => {
  const sections = Object.keys(PermissionRoleSections).map(
    (entityName, index) => ({
      isInitiallyUncollapsed: index === 0,
      collapsible: true,
      name: getHumanReadableEntityName(entityName, true, true),
      entityName,
      rows: Object.keys(PermissionRoleSections[entityName]).map(
        (permission) => ({
          name: permission,
          entityName,
          permission,
        }),
      ),
    }),
  );

  const premiumFilteredSections = usePremiumFeatureStatus(
    sections,
    'entityName',
    'categories',
  );

  return premiumFilteredSections;
};

const cascadeUpdatePermissions = (
  permissionRole: PermissionRole,
  entityName: string,
  permission: string,
  type: string,
): Partial<PermissionRoleSetting>[] => {
  const extraPermissions: Partial<PermissionRoleSetting>[] = [];
  if (permission === 'view') {
    const extraPermissionsKeys = Object.keys(
      PermissionRoleSettingsPostParameters[entityName],
    ).filter((key) => key !== 'view');
    for (const key of extraPermissionsKeys) {
      const existingPermissionRoleSetting:
        | PermissionRoleSetting
        | undefined = permissionRole.permission_role_settings.find(
        (permissionRolSetting) =>
          permissionRolSetting.entity_name ===
            PermissionRoleSettingsPostParameters[entityName][key]
              .entity_name &&
          permissionRolSetting.action ===
            PermissionRoleSettingsPostParameters[entityName][key]
              .action,
      );
      if (!existingPermissionRoleSetting) {
        continue;
      }

      const existingPermissionRoleSettingType =
        existingPermissionRoleSetting.type;
      const existingPermissionRoleSettingAction =
        existingPermissionRoleSetting.action;

      if (
        type === 'none' &&
        existingPermissionRoleSettingType !== 'none'
      ) {
        extraPermissions.push({
          ...PermissionRoleSettingsPostParameters[entityName][key],
          type: 'none' as PermissionRoleSettingType,
        });
      }
      if (
        type === 'if_assigned' &&
        (existingPermissionRoleSettingType ===
          'if_assigned_to_collection' ||
          existingPermissionRoleSettingType === 'all')
      ) {
        extraPermissions.push({
          ...PermissionRoleSettingsPostParameters[entityName][key],
          type: existingPermissionRoleSettingAction.includes('create')
            ? ('none' as PermissionRoleSettingType)
            : ('if_assigned' as PermissionRoleSettingType),
        });
      }
      if (
        type === 'if_assigned_to_collection' &&
        existingPermissionRoleSettingType === 'all'
      ) {
        extraPermissions.push({
          ...PermissionRoleSettingsPostParameters[entityName][key],
          type: existingPermissionRoleSettingAction.includes('create')
            ? ('none' as PermissionRoleSettingType)
            : ('if_assigned_to_collection' as PermissionRoleSettingType),
        });
      }
    }
  }
  return extraPermissions;
};

/**
 * @description Function to get items for EntityActionUpdateDropdownPill
 */
export const getItems = (
  permissionRole: PermissionRole,
  entityName: string,
  permission: string,
  create: APICreateAction<'permission_role_setting'>,
) => {
  const items = [
    {
      label: LABELS_FOR_PERMISSIONS.all,
      onClick: () => {
        create(
          [
            {
              type: 'all',
              ...PermissionRoleSettingsPostParameters[entityName][
                permission
              ],
            },
            ...cascadeUpdatePermissions(
              permissionRole,
              entityName,
              permission,
              'all',
            ),
          ] as any,
          {},
          permissionRole.entityName,
          permissionRole.guid,
        );
      },
    },
    {
      label: LABELS_FOR_PERMISSIONS.none,
      onClick: () => {
        create(
          [
            {
              type: 'none',
              ...PermissionRoleSettingsPostParameters[entityName][
                permission
              ],
            },
            ...cascadeUpdatePermissions(
              permissionRole,
              entityName,
              permission,
              'none',
            ),
          ] as any,
          {},
          permissionRole.entityName,
          permissionRole.guid,
        );
      },
    },
  ];
  if (
    permission !== 'create' &&
    entityName !== 'tag' &&
    entityName !== 'group' &&
    entityName !== 'entity_state_workflow'
  ) {
    const label = 'If Assigned';
    items.splice(1, 0, {
      label,
      onClick: () => {
        create(
          [
            {
              type: PermissionRoleSettingType.if_assigned,
              ...PermissionRoleSettingsPostParameters[entityName][
                permission
              ],
            },
            ...cascadeUpdatePermissions(
              permissionRole,
              entityName,
              permission,
              'if_assigned',
            ),
          ] as any,
          {},
          permissionRole.entityName,
          permissionRole.guid,
        );
      },
    });
  }
  if (
    (entityName === 'experiment_workflow' ||
      entityName === 'protocol_collection') &&
    permission !== 'create'
  ) {
    const label = 'If Assigned To Collection';
    items.splice(1, 0, {
      label,
      onClick: () => {
        create(
          [
            {
              type: PermissionRoleSettingType.if_assigned_to_collection,
              ...PermissionRoleSettingsPostParameters[entityName][
                permission
              ],
            },
            ...cascadeUpdatePermissions(
              permissionRole,
              entityName,
              permission,
              'if_assigned_to_collection',
            ),
          ] as any,
          {},
          permissionRole.entityName,
          permissionRole.guid,
        );
      },
    });
  }
  return items;
};

export const getExistingPermissionRoleSettingType = (
  permissionRole: {
    permission_role_settings: PartialPermissionRoleSetting[];
  },
  entityName: string,
  permission: string,
) => {
  const entityNameAction =
    PermissionRoleSettingsPostParameters[entityName][permission];

  if (entityNameAction) {
    const existingPermissionRoleSetting:
      | PartialPermissionRoleSetting
      | undefined = permissionRole.permission_role_settings.find(
      (permissionRoleSetting) =>
        permissionRoleSetting &&
        permissionRoleSetting.entity_name ===
          entityNameAction.entity_name &&
        permissionRoleSetting.action === entityNameAction.action,
    );

    if (existingPermissionRoleSetting) {
      return LABELS_FOR_PERMISSIONS[
        existingPermissionRoleSetting.type as keyof typeof LABELS_FOR_PERMISSIONS
      ];
    }
  }

  return LABELS_FOR_PERMISSIONS.none;
};
